/** @format */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useNavigate} from 'react-router-dom';

import {ReportOrderSimple, type ReportOrderSimpleValues} from 'ui';
import {ROUTES} from 'constants/ROUTES';
import {useSearchParams} from 'helpers/routerHelpers';

import {ERROR_REASON_LABEL, type ReportErrorReason} from '../content';
import {
  useCreateProjectReportMutation,
  useOrderCompaniesQuery,
  useOrderReportStatusQuery,
} from '../graphql/__generated__';
import {lastClosedMonth, parseRuDate} from './helpers/period';

/** Как часто спрашиваем сервер о стадии. Сборка идёт минутами. */
const POLL_MS = 5000;

const BAD_PERIOD = 'Период задан неверно: ждём даты вида\u00a001.06.2026.';
const BAD_ORDER = 'Начало периода позже его\u00a0конца.';
const MUTATION_FAILED = 'Не удалось запустить сборку. Попробуйте ещё\u00a0раз.';
// Ровно два слова: NBSP по правилу склеивает последние два слова начиная
// с трёх, здесь переноситься самому по себе уже нечему.
const BAD_COMPANY = 'Выберите проект.';

/**
 * Запуск сборки отчёта.
 *
 * Страница не подчинена проекту: проект здесь выбирают, а GET-параметр лишь
 * предустанавливает выбор — так по ссылке из карточки проекта человек
 * попадает в форму с уже нужным кабинетом, но может передумать.
 */
const Order = () => {
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const requestedCompanyId = searchParams.get('companyId');

  const {data: companiesData} = useOrderCompaniesQuery();
  const companies = useMemo(
    () =>
      (companiesData?.currentUser?.companies ?? [])
        .map(item => ({id: item.id, name: item.name}))
        // Порядок с сервера не документирован, а искать проект глазами в
        // списке без порядка — то же, что читать несортированный справочник.
        // `localeCompare` с русской локалью, а не сравнение строк: иначе «Ё»
        // уезжает за «Я», а заглавные становятся отдельным алфавитом.
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [companiesData],
  );

  const [reportId, setReportId] = useState<string | null>(null);
  const [localFailure, setLocalFailure] = useState<string | null>(null);
  const [create] = useCreateProjectReportMutation();

  // Гард синхронный: `loading` мутации обновится только на следующем рендере,
  // а второй клик прилетает раньше.
  const submitting = useRef(false);

  const {data: statusData, stopPolling} = useOrderReportStatusQuery({
    variables: {id: reportId ?? ''},
    skip: !reportId,
    pollInterval: POLL_MS,
  });

  const status = statusData?.currentUser?.projectReport?.status;
  const reason = statusData?.currentUser?.projectReport?.errorReason as
    | ReportErrorReason
    | null
    | undefined;

  // Терминальную стадию переспрашивать незачем: без этого вкладка стучалась
  // бы на сервер каждые пять секунд и после того, как отчёт собран.
  useEffect(() => {
    if (status === 'READY' || status === 'ERROR') stopPolling();
  }, [status, stopPolling]);

  const initialValues = useMemo((): ReportOrderSimpleValues => {
    const {from, to} = lastClosedMonth(new Date());
    return {companyId: requestedCompanyId ?? '', periodFrom: from, periodTo: to};
  }, [requestedCompanyId]);

  const state =
    localFailure != null || status === 'ERROR'
      ? 'failed'
      : status === 'READY'
        ? 'done'
        : reportId != null
          ? 'building'
          : 'idle';

  // Индексируем через Partial: `reason` приходит с сервера непроверенным
  // кастом, и стадия вне четырёх известных не должна тихо давать `undefined`
  // и включать дефолт пропа `failureText` каталога — это чужая причина.
  const failureText =
    localFailure ??
    (reason != null
      ? ((ERROR_REASON_LABEL as Partial<Record<ReportErrorReason, string>>)[reason] ??
        MUTATION_FAILED)
      : undefined);

  const handleSubmit = useCallback(
    (values: ReportOrderSimpleValues) => {
      if (submitting.current) return;

      // GET-параметр подставляется на веру (см. `initialValues` выше) — Select
      // рисует подпись через `options.find(...)?.label ?? ''`, поэтому чужой
      // или отсутствующий id молча выглядит как «не выбрано», а не как
      // ошибка. Сверяем с реальным списком проектов пользователя здесь, а не
      // полагаемся на то, что форма его когда-либо показала.
      const isKnownCompany = companies.some(company => company.id === values.companyId);
      if (!values.companyId || !isKnownCompany) {
        setLocalFailure(BAD_COMPANY);
        return;
      }

      const periodStart = parseRuDate(values.periodFrom);
      const periodEnd = parseRuDate(values.periodTo);

      if (periodStart == null || periodEnd == null) {
        setLocalFailure(BAD_PERIOD);
        return;
      }
      if (periodStart > periodEnd) {
        setLocalFailure(BAD_ORDER);
        return;
      }

      submitting.current = true;
      setLocalFailure(null);

      create({variables: {companyId: values.companyId, periodStart, periodEnd}})
        .then(result => {
          const created = result.data?.createProjectReport;
          if (created) setReportId(created.id);
          else setLocalFailure(MUTATION_FAILED);
        })
        .catch(() => setLocalFailure(MUTATION_FAILED))
        .finally(() => {
          submitting.current = false;
        });
    },
    [create, companies],
  );

  const handleOpenResult = useCallback(() => {
    if (reportId) navigate(`/${ROUTES.REPORT}?id=${encodeURIComponent(reportId)}`);
  }, [navigate, reportId]);

  const handleLogoClick = useCallback(() => navigate(ROUTES.INDEX), [navigate]);

  return (
    <ReportOrderSimple
      initialValues={initialValues}
      companies={companies}
      state={state}
      failureText={failureText}
      onSubmit={handleSubmit}
      onOpenResult={handleOpenResult}
      onLogoClick={handleLogoClick}
    />
  );
};

export default Order;
